<template>
    <div v-if="model">
        <rkm-row>
            <rkm-col cols="12">
                <rkm-table
                    :busy="busy"
                    :fields="fields"
                    :filter="filter"
                    :items="filteredItems"
                    :tbody-tr-class="rowClass"
                    class="tabela-paciente"
                    ref="RkmTable"
                >
                    <template v-slot:filters>
                        <rkm-row>
                            <rkm-col cols="12">
                                <rkm-form-input
                                    label="Buscar"
                                    placeholder="Digite para buscar"
                                    v-model="filter"
                                />
                            </rkm-col>
                        </rkm-row>

                        <rkm-row>
                            <rkm-col>
                                <rkm-form-select
                                    :options="form.temposEspera"
                                    default-option-text="Todos"
                                    label="Tempo de espera"
                                    show-default-option
                                    text-field="minutos"
                                    v-model="model.tempoEsperaId"
                                    value-field="id"
                                />
                            </rkm-col>

                            <rkm-col>
                                <rkm-form-select
                                    :options="form.opcoesEntrada"
                                    default-option-text="Todas"
                                    label="Opção de entrada"
                                    show-default-option
                                    text-field="nome"
                                    v-model="model.opcaoEntradaId"
                                    value-field="id"
                                />
                            </rkm-col>

                            <rkm-col>
                                <rkm-form-select
                                    :options="form.prioridades"
                                    default-option-text="Todas"
                                    label="Prioridade"
                                    show-default-option
                                    text-field="nome"
                                    v-model="model.prioridadeId"
                                    value-field="id"
                                />
                            </rkm-col>

                            <rkm-col>
                                <rkm-form-select
                                    :options="form.status"
                                    default-option-text="Todos"
                                    label="Status"
                                    show-default-option
                                    text-field="nome"
                                    v-model="model.statusId"
                                    value-field="id"
                                />
                            </rkm-col>
                        </rkm-row>
                    </template>

                    <template v-slot:cell(tempoEspera)="{item}">
                        <rkm-icon
                            v-if="item.tempoEspera.id"
                            :id="uniqueId('tempo-espera-tooltip')"
                            :style="'color: ' + item.tempoEspera.cor"
                            :title="formatTempoEspera(item.tempoEspera.total)"
                            font-scale="2"
                            icon="battery-full"
                            v-rkm-tooltip.hover
                        />

                        <span v-else>
                            -
                        </span>
                    </template>

                    <template v-slot:cell(pacienteNome)="{item}">
                        {{ item.pacienteNome }}

                        <span
                            v-if="item.pacienteObitoEm"
                            class="ml-1"
                            title="Paciente em óbito"
                            v-rkm-tooltip.hover
                        >
                            <rkm-icon icon="cross" />
                        </span>

                        <span
                            v-if="item.pacienteBloqueado"
                            class="ml-1"
                            title="Cadastro bloqueado"
                            v-rkm-tooltip.hover
                        >
                            <rkm-icon icon="ban" />
                        </span>

                        <span
                            v-if="isProvisorioOuTemporario(item)"
                            class="ml-1"
                            title="Paciente provisório/temporário"
                            v-rkm-tooltip.hover
                        >
                            <rkm-icon icon="hourglass-half" />
                        </span>

                        <span
                            v-if="item.pacienteClassificacaoId == enums.classificacao.OUTRO_MUNICIPIO"
                            class="ml-1"
                            title="Paciente não munícipe"
                            v-rkm-tooltip.hover
                        >
                            <rkm-icon icon="map-pin" />
                        </span>

                        <span
                            v-if="isSemCpfCns(item)"
                            class="ml-1"
                            title="Paciente sem CPF e CNS"
                            v-rkm-tooltip.hover
                        >
                            <rkm-icon icon="exclamation-triangle" />
                        </span>

                        <span
                            v-if="item.pacienteResponsavelFamiliar"
                            class="ml-1"
                            title="Responsável familiar"
                            v-rkm-tooltip.hover
                        >
                            <rkm-icon icon="crown" />
                        </span>

                        <span
                            v-if="isIdoso(item.pacienteDataNascimento)"
                            class="ml-1"
                            title="Idoso"
                            v-rkm-tooltip.hover
                        >
                            <rkm-icon icon="blind" />
                        </span>

                        <span
                            v-if="hasTea(item)"
                            class="ml-1"
                            title="Pessoa com Transtorno do Espectro Autista - TEA"
                            v-rkm-tooltip.hover
                        >
                            <rkm-icon icon="infinity" />
                        </span>

                        <span
                            v-if="hasPcd(item)"
                            class="ml-1"
                            title="Pessoa com Deficiência - PCD"
                            v-rkm-tooltip.hover
                        >
                            <rkm-icon icon="wheelchair" />
                        </span>
                    </template>

                    <template v-slot:cell(actions)="{item}">
                        <template v-if="form.telas.length > 0">
                            <rkm-dropdown
                                v-if="form.telas.length > 1"
                                :color="item.chamadas > 0 ? 'secondary' : 'info'"
                                class="mr-2"
                                small
                                title="Chamar"
                                v-rkm-tooltip.hover
                            >
                                <template v-slot:button-content>
                                    <rkm-icon icon="volume-up" />

                                    <rkm-badge v-if="item.chamadas > 0">
                                        {{ item.chamadas }}
                                    </rkm-badge>
                                </template>

                                <rkm-dropdown-item
                                    v-for="tela in form.telas"
                                    @click="call(item.id, tela.id)"
                                    v-bind:key="tela.id"
                                >
                                    {{tela.nome}}
                                </rkm-dropdown-item>
                            </rkm-dropdown>

                            <rkm-button
                                v-else
                                :color="item.chamadas > 0 ? 'secondary' : 'info'"
                                @click="call(item.id, form.telas[0].id)"
                                small
                                title="Chamar"
                            >
                                <rkm-icon icon="volume-up" />

                                <rkm-badge v-if="item.chamadas > 0">
                                    {{item.chamadas}}
                                </rkm-badge>
                            </rkm-button>
                        </template>

                        <rkm-button
                            @click="dispensar(item)"
                            authorize="store-farmacia-dispensacao"
                            color="primary"
                            small
                            title="Dispensar"
                        >
                            <rkm-icon icon="hand-holding-medical" />
                        </rkm-button>

                        <rkm-button
                            :disabled="!item.pacienteId"
                            @click="$refs.ModalRecepcao.open(item.id)"
                            authorize="update-farmacia-fila-atendimento"
                            color="primary"
                            small
                            title="Editar"
                        >
                            <rkm-icon icon="pen" />
                        </rkm-button>

                        <rkm-button
                            @click="$refs.ModalEncaminhamento.open(item)"
                            color="primary"
                            small
                            title="Encaminhar atendimento"
                        >
                            <rkm-icon icon="location-arrow" />
                        </rkm-button>

                        <rkm-button
                            @click="$refs.ModalEvasao.open(item.id)"
                            color="danger"
                            small
                            title="Evadir"
                        >
                            <rkm-icon icon="running" />
                        </rkm-button>
                    </template>
                </rkm-table>
            </rkm-col>
        </rkm-row>

        <rkm-row class="mt-3">
            <rkm-col>
                <p class="m-0">
                    <span class="legend-color purple" /> Paciente em óbito<br>
                    <span class="legend-color danger" /> Cadastro bloqueado<br>
                    <span class="legend-color orange" /> Paciente provisório ou temporário<br>
                    <span class="legend-color brown" /> Paciente não munícipe<br>
                    <span class="legend-color warning" /> Paciente sem CPF e CNS
                </p>
            </rkm-col>
        </rkm-row>

        <ModalRecepcao ref="ModalRecepcao" />

        <ModalChamada
            :salas="form.salas"
            ref="ModalChamada"
        />

        <ModalEncaminhamento ref="ModalEncaminhamento" />
        <ModalEvasao ref="ModalEvasao" />
    </div>
</template>

<script>
// Components.
import ModalChamada from './components/chamada/Modal'
import ModalEvasao from './components/evasao/Modal'
import ModalRecepcao from './components/recepcao/Modal'
import ModalEncaminhamento from './components/encaminhamento/Modal'

// Enums.
import modulo from '@/enums/administracao/modulo'
import classificacao from '@/enums/cadastro/paciente/classificacao'
import subclassificacao from '@/enums/cadastro/paciente/subclassificacao'
import deficienciaBase from '@/enums/cadastro/cidadao/deficienciaBase'
import deficiencia from '@/enums/cadastro/cidadao/deficiencia'

// Libs.
import moment from 'moment'

// Mixins.
import {error, repository, stringCase, websocket} from '@/mixins'

// Vuex.
import {mapActions, mapGetters} from 'vuex'

export default {
    components: {
        ModalChamada,
        ModalEvasao,
        ModalRecepcao,
        ModalEncaminhamento,
    },
    computed: {
        ...mapGetters({
            centroCusto: 'getCentroCusto',
            customer: 'getCustomer',
        }),
        filteredItems() {
            let fila = this.model.fila

            if (this.model.tempoEsperaId) {
                fila = fila.filter(item => item.tempoEspera.id === this.model.tempoEsperaId)
            }

            if (this.model.opcaoEntradaId) {
                fila = fila.filter(item => item.opcaoEntradaId === this.model.opcaoEntradaId)
            }

            if (this.model.prioridadeId) {
                fila = fila.filter(item => item.prioridadeId === this.model.prioridadeId)
            }

            if (this.model.statusId) {
                fila = fila.filter(item => item.statusId === this.model.statusId)
            }

            return fila
        },
    },
    created() {
        this.repositories = {
            chamada: this.getConectaSusRepository('farmacia.chamada'),
            filaAtendimento: this.getConectaSusRepository('farmacia.filaAtendimento'),
        }
    },
    data: () => ({
        additionals: null,
        busy: false,
        enums: {modulo, classificacao, subclassificacao, deficienciaBase, deficiencia},
        fields: [
            {key: 'tempoEspera', label: 'Tempo de espera'},
            {key: 'opcaoEntradaNome', label: 'Opção de entrada'},
            {key: 'senha', label: 'Senha'},
            {key: 'prioridadeNome', label: 'Prioridade'},
            {key: 'horarioChegada', label: 'Horário de chegada'},
            {key: 'pacienteNome', label: 'Nome'},
            {key: 'pacienteIdade', label: 'Idade'},
            {key: 'pacienteProntuario', label: 'Prontuário'},
            {class: 'col-min text-center', key: 'actions', label: 'Ações'},
        ],
        filter: null,
        form: null,
        model: null,
    }),
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        init() {
            this.pusher.subscribe(`private-${this.customer.link}-painel-senha-fila`)
                .bind(`${this.centroCusto.setor.tipoId}.${this.centroCusto.id}.${this.enums.modulo.FARMACIA}.updated`, (data) => {
                    this.model.fila = Object.assign(this.snakeObjectKeysToCamelRecursive(data.fila))
                })
        },
        load() {
            this.setLoading()

            this.repositories.filaAtendimento.list()
                .then(response => {
                    this.form = Object.assign(response.data.form)
                    this.model = Object.assign(response.data.model)

                    this.$emit('additionals', this.additionals)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        call(filaId, telaId) {
            const request = {
                filaId: filaId,
                telaId: telaId,
            }

            if (this.form.salas.length > 0) {
                this.$refs.ModalChamada.open(filaId, telaId)
            } else {
                this.busy = true

                this.repositories.chamada.store(this.camelObjectKeysToSnakeRecursive(request))
                    .catch(this.handleError)
                    .finally(() => this.busy = false)
            }
        },
        dispensar(item) {
            if (item.pacienteId) {
                this.$router.push({name: 'farmacia-dispensacao-create', params: {pacienteId: item.pacienteId, filaId: item.id}})
            } else {
                this.$refs.ModalRecepcao.open(item.id, true)
            }
        },
        destroy(filaId) {
            const destroy = () => this.repositories.fila.destroy(filaId)

            this.showDeleteConfirmation('Deseja realmente excluir a senha?', destroy)
        },
        formatTempoEspera(value) {
            let format = 'Esperando por'

            if (value.dias !== null) {
                format = `${format} ${value.dias} dia(s)`
            }

            if (value.horas !== null) {
                format = `${format} ${value.horas} hora(s)`
            }

            if (value.minutos !== null) {
                if (value.horas !== null) {
                    format = `${format} e ${value.minutos} minuto(s)`
                } else {
                    format = `${format} ${value.minutos} minuto(s)`
                }
            }

            return format
        },
        isProvisorioOuTemporario(item) {

            return item.pacienteClassificacaoId == this.enums.classificacao.PROVISORIO
                || item.pacienteSubclassificacaoId == this.enums.subclassificacao.TEMPORARIO
        },
        isSemCpfCns(item) {

            return !!item.pacienteId && !item.pacienteCpf && !item.pacienteCns
        },
        isIdoso(dataNascimento) {

            return !!dataNascimento && moment().diff(moment(dataNascimento, 'DD/MM/YYYY'), 'years') >= 60
        },
        getDeficienciasBaseIds(item) {

            return item.pacienteDeficienciasBaseIds
                ? item.pacienteDeficienciasBaseIds.split(',').map(Number)
                : []
        },
        hasTea(item) {

            return this.getDeficienciasBaseIds(item).includes(this.enums.deficienciaBase.TEA)
                || item.pacienteDeficienciaId === this.enums.deficiencia.AUTISMO
        },
        hasPcd(item) {

            return this.getDeficienciasBaseIds(item).some(id => id !== this.enums.deficienciaBase.TEA)
                || (!!item.pacienteDeficienciaId && item.pacienteDeficienciaId !== this.enums.deficiencia.AUTISMO)
        },
        rowClass(item, type) {
            if (item && type === 'row') {
                if (item.pacienteObitoEm) {
                    return 'table-purple'
                }
                if (item.pacienteBloqueado) {
                    return 'table-danger'
                }
                if (this.isProvisorioOuTemporario(item)) {
                    return 'table-orange'
                }
                if (item.pacienteClassificacaoId == this.enums.classificacao.OUTRO_MUNICIPIO) {
                    return 'table-brown'
                }
                if (this.isSemCpfCns(item)) {
                    return 'table-warning'
                }
            }
        },
    },
    mixins: [error, repository, stringCase, websocket],
    mounted() {
        this.init()
        this.load()
    },
}
</script>

<style scoped>
.legend-color {
    display: inline-block;
    width: 12px;
    height: 12px;
    border-radius: 2px;
    margin-right: 4px;
}
.legend-color.purple {
    background-color: #6f42c1;
}
.legend-color.danger {
    background-color: var(--danger);
}
.legend-color.orange {
    background-color: #F28C28;
}
.legend-color.brown {
    background-color: #6F4E37;
}
.legend-color.warning {
    background-color: var(--warning);
}

.tabela-paciente >>> .table-orange {
    border-color: #F28C28;
    background-color: #FFE0B2;
}

.tabela-paciente >>> .table-brown {
    border-color: #6F4E37;
    background-color: #D2B48C;
}

.tabela-paciente >>> .table-purple {
    border-color: #6f42c1;
    background-color: #E1D4F2;
}
</style>
